import type { HowItWorksLine, Objective } from '../api/missions';
import { ca } from '../i18n/ca';

/**
 * The lines a player may read: a line about an objective the players cannot
 * see (hidden, silent, or not in their copy at all) stays out. A player's copy
 * already comes without it (MissionService.maskHidden); whoever directs the
 * night gets the whole definition, and the players' page must not show it
 * more than the game does.
 */
function visibleHowItWorks(
  lines: HowItWorksLine[] | undefined,
  objectives: Objective[],
): HowItWorksLine[] {
  const seen = new Set(
    objectives
      .filter(
        (o) =>
          !o.silent &&
          o.priority !== 'hidden' &&
          (o as { visibility?: string }).visibility !== 'hidden',
      )
      .map((o) => o.id),
  );
  return (lines ?? []).filter((l) => !l.objective || seen.has(l.objective));
}

/**
 * «Com funciona aquesta nit» (MECHANICS.md R18, step D1.3): the lines the
 * platform wrote into the briefing. Open on the orders page, where it is the
 * first card; folded on the live session page, under NOW. Nothing when the
 * definition has none.
 */
export function HowItWorksCard({
  lines,
  objectives,
  collapsed = false,
}: {
  lines?: HowItWorksLine[];
  objectives: Objective[];
  collapsed?: boolean;
}) {
  const shown = visibleHowItWorks(lines, objectives);
  if (shown.length === 0) return null;
  const list = (
    <ul className="how-it-works">
      {shown.map((l) => (
        <li key={l.id}>{l.text}</li>
      ))}
    </ul>
  );
  const title = ca.howItWorks.title;
  return (
    <article className="card" aria-label={title}>
      {collapsed ? (
        <details className="how-it-works">
          <summary>
            <h2>{title}</h2>
          </summary>
          {list}
        </details>
      ) : (
        <>
          <h2>{title}</h2>
          {list}
          <p className="muted small">{ca.howItWorks.hint}</p>
        </>
      )}
    </article>
  );
}
